import React, {Component} from "react"
import { MathKatex2 } from "../../components/MathKatex"
import "./AnswersGroup.css"

interface Option {
    label: string
    value: string
}

interface Props {
    questionNumber: number
    options: Option[]
    selectedAnswer: string
    allowCrossOut: boolean
    crossedOutInfo: any
    onSelect: (value: string) => void
    onCrossOut: (choice: string, crossed: boolean) => void
}

interface State {
    selectedValue: string
}

/**
 * Bellringer answer group similart to SAT
 */
export default class AnswersGroup extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            selectedValue: props.selectedAnswer,
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (prevProps.questionNumber !== this.props.questionNumber || prevProps.selectedAnswer !== this.props.selectedAnswer) {
            this.setState({ selectedValue: this.props.selectedAnswer })
        }
    }

    handleSelect = (value: string) => {
        this.setState({ selectedValue: value })
        this.props.onSelect(value)
    }

    handleCrossOut = (choice, crossed) => {
        this.props.onCrossOut(choice, crossed)
    }

    render() {
        let {questionNumber, selectedAnswer, allowCrossOut, crossedOutInfo,} = this.props

        return (
            <div className="answers-group">
                {
                    this.props.options.map((option, index) => {
                        let {selectedValue} = this.state
                        let isSelected = !!selectedValue && selectedValue === option.value

                        return (
                            <Answer
                                key={`answer-${questionNumber}-${option.value}`}
                                value={option.value}
                                label={option.label}
                                selected={isSelected}
                                allowCrossOut={allowCrossOut}
                                crossedOutInfo={crossedOutInfo}
                                onSelect={this.handleSelect}
                                onCrossOut={this.handleCrossOut}
                            />
                        )
                    })
                }
            </div>
        )
    }
}

//--- Helpers ---
interface IAnswerProps {
    value: string
    label: string
    selected: boolean
    allowCrossOut: boolean
    crossedOutInfo: any
    onSelect: (value) => void
    onCrossOut: (choice: string, crossed: boolean) => void
}

interface IAnswerState {
    crossedOut: boolean
    selected: boolean
}

/**
 * This component will be emulating the SAT answer behavior for Bellringers
 */
class Answer extends Component<IAnswerProps, IAnswerState> {
    state = {
        crossedOut: false,
        selected: false,
    }

    componentDidMount(): void {
        this.initialize()
    }

    componentDidUpdate(prevProps: Readonly<IAnswerProps>, prevState: Readonly<IAnswerState>, snapshot?: any): void {
        if (
            prevProps.label !== this.props.label ||
            prevProps.allowCrossOut !== this.props.allowCrossOut ||
            prevProps.selected !== this.props.selected ||
            prevProps.crossedOutInfo !== this.props.crossedOutInfo
        ) {
            this.initialize()
        }
    }

    initialize = () => {
        let {allowCrossOut, crossedOutInfo, selected} = this.props
        let crossedOut = false
        if (allowCrossOut && crossedOutInfo && !selected) {
            crossedOut = crossedOutInfo[this.props.value]
        }
        this.setState({crossedOut, selected})
    }

    toggleCrossOut = () => {
        let {crossedOut} = this.state
        crossedOut = !crossedOut
        this.setState({crossedOut})
        this.props.onCrossOut(this.props.value, crossedOut)
    }

    handleSelect = () => {
        let {value, onSelect} = this.props
        this.setState({crossedOut: false})
        onSelect(value)
    }

    render() {
        let {value, label, selected, allowCrossOut} = this.props
        let {crossedOut} = this.state

        return (
            <div key={value} className="answer-container">
                 <div className={`answer-section `}>
                    <button key={value} onClick={this.handleSelect} style={{width: "100%"}}>
                        <div className={`mc-option ${selected ? "selected" : ""} ${crossedOut ? "faded-option": ""}`}>
                            <span className="mc-circle">{value}</span>
                            <span className="mc-label"><MathKatex2 mixedContent={label} /></span>
                        </div>
                    </button>
                    <div className={`${crossedOut ? "crossed-out": ""}`} />
                </div>
                { allowCrossOut && 
                    <div className="crossed-out-section">
                        <CrossOutButton text={value} crossedOut={crossedOut} onCrossOut={this.toggleCrossOut} /> 
                    </div>
                }
            </div>
        )
    }
}

/**
 * Allows the user to cross-out an answer
 */
function CrossOutButton(props) {
    let {text, crossedOut, onCrossOut} = props

    return (
        <div className="icon-wrapper" onClick={onCrossOut} role="button">
            {!crossedOut ?
                <>
                    <div className="icon-box">
                        <span>{text}</span>
                    </div>
                    <div className="strike-line"></div>
                </>
                :
                <span className={`crossed-out-label undo`}>Undo</span>
            }
        </div>
    )
}

interface IFillInAnswerProps {
    questionNumber: number
    fillInAnswer: string
    onFillInAnswer: (answer: string) => void
}

interface IFillInAnswerState {
    inputValue: string
}

class FillInAnswer extends React.Component<IFillInAnswerProps, IFillInAnswerState> {
    inputRef = React.createRef<HTMLInputElement>()

    constructor(props) {
        super(props)
        this.state = {
            inputValue: this.props.fillInAnswer
        }
    }

    componentDidMount() {
        if (this.inputRef.current) {
            this.inputRef.current.focus()
        }
    }

    componentDidUpdate(prevProps: Readonly<IFillInAnswerProps>) {
        if (prevProps.questionNumber !== this.props.questionNumber || prevProps.fillInAnswer !== this.props.fillInAnswer) {
            this.setState({inputValue: this.props.fillInAnswer})
            if (this.inputRef.current) {
                this.inputRef.current.focus()
            }
        }
    }

    handleInputChange = (e) => {
        let {onFillInAnswer} = this.props
        let value = e.target.value
        let regex = /^[-0-9./]+$/

        if (!value) {
            this.setState({inputValue: ""})
            onFillInAnswer("")
            return
        }

        // Check for acceptable input values for SAT
        if (regex.test(value)) {
            let lenPos = 5
            let lenNeg = 6

            if (value.includes(".")) {
                lenPos++
                lenNeg++
            }

            if ((value.startsWith("-") && value.length <= lenNeg) || (!value.startsWith("-") && value.length <= lenPos)) {
                onFillInAnswer(value)
                this.setState({inputValue: value})
            }
        }
    }

    render() {
        let {inputValue} = this.state

        return (
            <div className="fill-in-answer">
                <input ref={this.inputRef} type="text" onChange={this.handleInputChange} value={inputValue} size={6} />

                <div className="answer-preview">
                    <b>Answer Preview: {inputValue}</b>
                </div>
            </div>
        )
    }
}
